import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, CheckCircle, XCircle, Database, Loader2, Link, Search } from 'lucide-react';
import { 
  checkCustomerDataIntegrity, 
  restoreAllIncompleteCustomers, 
  findOrphanedData, 
  attemptDataReconnection,
  CustomerDataCheck,
  OrphanedData 
} from '../utils/dataRecovery';

export default function DataRecoveryPanel() {
  const [isChecking, setIsChecking] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [customerChecks, setCustomerChecks] = useState<CustomerDataCheck[]>([]);
  const [orphanedData, setOrphanedData] = useState<OrphanedData | null>(null);
  const [restorationResult, setRestorationResult] = useState<any>(null);
  const [showPanel, setShowPanel] = useState(false);

  const handleCheckIntegrity = async () => {
    setIsChecking(true);
    try {
      const [checks, orphaned] = await Promise.all([
        checkCustomerDataIntegrity(),
        findOrphanedData()
      ]);
      
      setCustomerChecks(checks);
      setOrphanedData(orphaned);
      setShowPanel(true);
    } catch (error) {
      console.error('Erro ao verificar integridade:', error);
      alert('Erro ao verificar integridade dos dados');
    } finally {
      setIsChecking(false);
    }
  };

  const handleReconnectData = async () => {
    if (!window.confirm('Tem certeza que deseja tentar reconectar dados órfãos? Esta ação tentará vincular dados desconectados aos clientes corretos.')) {
      return;
    }

    setIsReconnecting(true);
    try {
      const result = await attemptDataReconnection();
      
      // Recheck integrity after reconnection
      const updatedChecks = await checkCustomerDataIntegrity();
      const updatedOrphaned = await findOrphanedData();
      
      setCustomerChecks(updatedChecks);
      setOrphanedData(updatedOrphaned);
      
      alert(`Reconexão concluída! ${result.reconnected} dados reconectados, ${result.failed} falharam.`);
    } catch (error) {
      console.error('Erro durante reconexão:', error);
      alert('Erro durante a reconexão dos dados');
    } finally {
      setIsReconnecting(false);
    }
  };

  const handleRestoreData = async () => {
    if (!window.confirm('Tem certeza que deseja restaurar os dados faltantes? Esta ação primeiro tentará reconectar dados órfãos e depois criará dados padrão para clientes incompletos.')) {
      return;
    }

    setIsRestoring(true);
    try {
      const result = await restoreAllIncompleteCustomers();
      setRestorationResult(result);
      
      // Recheck integrity after restoration
      const updatedChecks = await checkCustomerDataIntegrity();
      const updatedOrphaned = await findOrphanedData();
      
      setCustomerChecks(updatedChecks);
      setOrphanedData(updatedOrphaned);
      
      alert(`Restauração concluída! ${result.summary.totalReconnected} dados reconectados, ${result.summary.totalCreated} dados criados.`);
    } catch (error) {
      console.error('Erro durante restauração:', error);
      alert('Erro durante a restauração dos dados');
    } finally {
      setIsRestoring(false);
    }
  };

  const incompleteCustomers = customerChecks.filter(check => check.missingData.length > 0);
  const completeCustomers = customerChecks.filter(check => check.missingData.length === 0);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Database className="w-5 h-5 mr-2" />
          Verificação e Recuperação de Dados
        </h3>
        <div className="flex gap-2">
          <button
            onClick={handleCheckIntegrity}
            disabled={isChecking}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {isChecking ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analisando...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Analisar Dados
              </>
            )}
          </button>
          
          {orphanedData && (orphanedData.locations.length > 0 || orphanedData.technicalConfigs.length > 0 || orphanedData.financialTerms.length > 0) && (
            <button
              onClick={handleReconnectData}
              disabled={isReconnecting}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center"
            >
              {isReconnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reconectando...
                </>
              ) : (
                <>
                  <Link className="w-4 h-4 mr-2" />
                  Reconectar Dados
                </>
              )}
            </button>
          )}
          
          {incompleteCustomers.length > 0 && (
            <button
              onClick={handleRestoreData}
              disabled={isRestoring}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
            >
              {isRestoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restaurando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Restaurar Tudo
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {showPanel && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <div>
                  <p className="text-sm text-green-600">Completos</p>
                  <p className="text-2xl font-bold text-green-800">{completeCustomers.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                <div>
                  <p className="text-sm text-red-600">Incompletos</p>
                  <p className="text-2xl font-bold text-red-800">{incompleteCustomers.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Database className="w-5 h-5 text-blue-600 mr-2" />
                <div>
                  <p className="text-sm text-blue-600">Total</p>
                  <p className="text-2xl font-bold text-blue-800">{customerChecks.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
                <div>
                  <p className="text-sm text-orange-600">Dados Órfãos</p>
                  <p className="text-2xl font-bold text-orange-800">
                    {orphanedData ? 
                      orphanedData.locations.length + 
                      orphanedData.technicalConfigs.length + 
                      orphanedData.financialTerms.length + 
                      orphanedData.installments.length 
                      : 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Orphaned Data Details */}
          {orphanedData && (orphanedData.locations.length > 0 || orphanedData.technicalConfigs.length > 0 || orphanedData.financialTerms.length > 0 || orphanedData.installments.length > 0) && (
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="text-md font-semibold text-orange-800 mb-3 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Dados Órfãos Encontrados (Desvinculados)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="font-medium text-orange-700">Localizações:</p>
                  <p className="text-orange-600">{orphanedData.locations.length}</p>
                </div>
                <div>
                  <p className="font-medium text-orange-700">Config. Técnicas:</p>
                  <p className="text-orange-600">{orphanedData.technicalConfigs.length}</p>
                </div>
                <div>
                  <p className="font-medium text-orange-700">Termos Financeiros:</p>
                  <p className="text-orange-600">{orphanedData.financialTerms.length}</p>
                </div>
                <div>
                  <p className="font-medium text-orange-700">Parcelas:</p>
                  <p className="text-orange-600">{orphanedData.installments.length}</p>
                </div>
              </div>
              <p className="text-xs text-orange-600 mt-2">
                Estes dados existem no banco mas não estão vinculados a nenhum cliente. Use "Reconectar Dados" para tentar vinculá-los automaticamente.
              </p>
            </div>
          )}

          {/* Incomplete Customers List */}
          {incompleteCustomers.length > 0 && (
            <div>
              <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2 text-yellow-600" />
                Clientes com Dados Incompletos
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {incompleteCustomers.map((check) => (
                  <div key={check.customer.id} className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-800">{check.customer.full_name}</p>
                        <p className="text-sm text-gray-600">CPF: {check.customer.cpf}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-red-600">Faltando:</p>
                        <p className="text-xs text-red-500">{check.missingData.join(', ')}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Restoration Result */}
          {restorationResult && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="text-md font-semibold text-green-800 mb-2">Resultado da Restauração</h4>
              <div className="text-sm text-green-700 space-y-1">
                <p>• Dados reconectados: {restorationResult.summary.totalReconnected}</p>
                <p>• Dados criados: {restorationResult.summary.totalCreated}</p>
                <p>• Erros encontrados: {restorationResult.summary.totalErrors}</p>
                <p>• Total de clientes processados: {restorationResult.creation.total}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}